/**
 * PolicyRegulatory Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive policy and regulatory page with advanced features:
 * - Quarterly update charts for Key Policy Messages and CBI meetings
 * - Industry radar for regulatory insights
 * - Consultations management and display
 * - Quarterly data analysis and visualization
 * - Modal integration for detailed quarterly updates
 * - Smart quarter derivation from month data
 * - Dynamic chart data generation
 * - Responsive widget layout system
 * 
 * Features:
 * - Last four quarters data filtering and display
 * - Chart data processing with color coding
 * - Quarter calculation from month names
 * - PDF data integration for quarterly reports
 * - Industry radar component integration
 * - Consultations component with real-time data
 * - Modal system for detailed views
 * - Responsive design for various screen sizes
 * 
 * @returns {JSX.Element} PolicyRegulatory page component with comprehensive regulatory information
 */
import React, { useState, useEffect } from 'react';
import 'react-tabs/style/react-tabs.css';
import Divider from '../components/Common/Divider';
import QuarterlyUpdateChart from '../components/PolicyRegulatory/QuarterlyUpdateChart';
import QuarterlyUpdateModal from '../modals/QuarterlyUpdateModal';
import IndustryRadar from '../components/PolicyRegulatory/IndustryRadar';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Consultations from '../components/PolicyRegulatory/Consultations';
import Parser from 'html-react-parser';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './PolicyRegulatory.css';

const { FILE_NAMES } = require('../utils/constants');

/**
 * PolicyRegulatory component for displaying regulatory information,
 * quarterly updates, and policy messages with advanced charting
 */
function PolicyRegulatory() {
  const [quarterlyUpdateBlobContent, setQuarterlyUpdateBlobContent] = useState('');
  const [cbiBlobContent, setCbiBlobContent] = useState('');

  const [showQuarterlyUpdate, setShowQuarterlyUpdate] = useState(false);
  const [quarterlyUpdateData, setQuarterlyUpdateData] = useState([]);
  const [quarterlyUpdateTitle, setQuarterlyUpdateTitle] = useState('');
  //Data
  const [keyMessagesData, setKeyMessagesData] = useState([]);
  const [cbiUpdatesData, setcbiUpdatesDataData] = useState([]);
  const [consultationsData, setConsultationsData] = useState([]);
  const [pipelineData, setPipeLineData] = useState([]);




  useEffect(() => {
    const fetchFileInfoAndContent = async () => {
      try {
        // Check if local file loading is enabled
        const useLocalFiles = process.env.REACT_APP_USE_LOCAL_FILES === 'true';
        
        // Check if local file loading is enabled for quarterly updates

        //Key Policy Messages
        const keyPolicyMessagesResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.KEY_POLICY_MESSAGES);
        setKeyMessagesData(keyPolicyMessagesResult.fileContent);
        
        // CBI Quarterly Meeting
        const cbiQuarterlyUpdatesResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.CBI_QUARTERLY_UPDATES);
        setcbiUpdatesDataData(cbiQuarterlyUpdatesResult.fileContent);
        
        // Consultations - keep using API
        const consultationsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.CONSULTATIONS);
        setConsultationsData(consultationsResult.fileContent);
        
        // Triage - keep using API
        const pipeLineResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.TRIAGE);
        setPipeLineData(pipeLineResult.fileContent);

        const pageContentResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.PAGECONTENT);

        const quarterlyUpdateContent = pageContentResult.fileContent.find(item => item.id === 2);
        const cbiUpdateContent = pageContentResult.fileContent.find(item => item.id === 3);

        setQuarterlyUpdateBlobContent(quarterlyUpdateContent ? quarterlyUpdateContent.htmlContent : 'No content available');
        setCbiBlobContent(cbiUpdateContent ? cbiUpdateContent.htmlContent : 'No content available');

      } catch (err) {
        console.error('Error fetching policy regulatory data:', err);
      }
    };

    fetchFileInfoAndContent();
  }, []);

  // Function to derive quarter from month name
  const getQuarterFromMonth = (month) => {
    if (!month) return '';
    const monthIndex = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'].indexOf(month);
    if (monthIndex === -1) return '';
    return `Q${Math.floor(monthIndex / 3) + 1}`;
  };

  // Function to get last 4 quarters of data
  const getLastFourQuarters = (data) => {
    return data
      .map((item) => ({
        ...item,
        // If Quarter is missing but Month exists, derive it from Month
        Quarter: item.Quarter && item.Quarter.trim() !== '' 
          ? item.Quarter 
          : getQuarterFromMonth(item.Month || item.month)
      }))
      .filter((item) => item.Quarter && item.Quarter.trim() !== '') // Only include items with Quarter values
      .sort((a, b) => {
        // Sort by year first, then by quarter
        const yearA = parseInt(a.Year);
        const yearB = parseInt(b.Year);
        if (yearA !== yearB) return yearA - yearB;
        
        // Extract quarter number (Q1 -> 1, Q2 -> 2, etc.)
        const quarterA = parseInt(a.Quarter.replace('Q', ''));
        const quarterB = parseInt(b.Quarter.replace('Q', ''));
        return quarterA - quarterB;
      })
      .slice(-4); // Take the last 4 entries
  };

  const keyMessagesChartData = {
    labels: getLastFourQuarters(keyMessagesData)
      .map((item) => `${item.Quarter} ${item.Year}`),
    datasets: [
      {
        data: getLastFourQuarters(keyMessagesData)
          .map(() => 1),
        backgroundColor: ['#36B948', '#2f8e44', '#29a74c', '#1f7a35'],
      },
    ],
  };

  const cbiUpdatesChartData = {
    labels: getLastFourQuarters(cbiUpdatesData)
      .map((item) => `${item.Quarter} ${item.Year}`),
    datasets: [
      {
        data: getLastFourQuarters(cbiUpdatesData)
          .map(() => 1),
        backgroundColor: ['#36B948', '#2f8e44', '#29a74c', '#1f7a35'],
      },
    ],
  };

  // Function to handle opening the QuarterlyUpdate with specific data
  const handleOpenQuarterlyUpdate = (title, data) => {
    setQuarterlyUpdateTitle(title);
    setQuarterlyUpdateData(data);
    setShowQuarterlyUpdate(true);
  };

  // Function to close the QuarterlyUpdate
  const handleCloseQuarterlyUpdate = () => {
    setShowQuarterlyUpdate(false);
    setQuarterlyUpdateData([]);
    setQuarterlyUpdateTitle('');
  };

  return (
    <div className='totalPage'>
      <a name="policyupdate"></a>
      <section id="KeyMessagesCBISection" className="wrapper">
        <div className="container mt-4">
          <section className="widget-container ">
            {keyMessagesData.length > 0 ? (
              <>
                <div className="widget key-messages-widget">
                  <QuarterlyUpdateChart
                    imageUrl="/assets/img/stockimages/KeyMessages.jpg"
                    title="Key Policy Messages"
                    description={Parser(quarterlyUpdateBlobContent)}
                    chartId="keyMessagesChart"
                    chartData={keyMessagesChartData}
                    pdfData={keyMessagesData}
                    chartStyle={{
                      width: '300px',
                      margin: '0 auto',
                    }}
                  />
                  <div className="show-more">
                    <button
                      onClick={() => handleOpenQuarterlyUpdate('Key Policy Messages', keyMessagesData, "KeyMessages")}
                      className="btn btn-primary"
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                      aria-label="Show more details about Key Policy Message"
                    >
                      <i className="fa-solid fa-arrow-right"></i> Show More
                    </button>

                  </div>
                </div>

                <div className="widget cbi-updates-widget">
                  <QuarterlyUpdateChart
                    imageUrl="/assets/img/stockimages/CBIUpdates.jpg"
                    title="CBI Quarterly Meeting"
                    description={Parser(cbiBlobContent)}
                    chartId="cbiUpdatesChart"
                    chartData={cbiUpdatesChartData}
                    pdfData={cbiUpdatesData}
                    chartStyle={{
                      width: '300px',
                      margin: '0 auto',
                    }}
                  />

                  <div className="show-more">
                    <button
                      onClick={() => handleOpenQuarterlyUpdate("CBI Quarterly Meeting", cbiUpdatesData, "CBIUpdates")}
                      className="btn btn-primary"
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                      aria-label="Show more details about CBI Quarterly Meeting"
                    >
                      <i className="fa-solid fa-arrow-right"></i> Show More
                    </button>   </div>

                </div>

                {showQuarterlyUpdate && (
              <QuarterlyUpdateModal
                title={quarterlyUpdateTitle}
                data={quarterlyUpdateData}
                onClose={handleCloseQuarterlyUpdate}
              />)}
              </>
            ) : (
              <LoadingSpinner text="Loading Quarterly Updates" />
            )}


          </section>
        </div>
      </section>

      <Divider height={80} />

      <section id="ConsultationsSection" className="wrapper bg-gradient-primary">
          <div id="consultations" className="container pb-10">
          <h2 className="text-uppercase main-header">Consultations, Papers and Submissions</h2>
        </div>
        <div className="container">
          {consultationsData.length > 0 ? (
            <Consultations data={consultationsData} />
          ) : (
            <LoadingSpinner text="Loading Consultations" />
          )}
        </div>
      </section>

      <Divider height={80} />


      <section id='ProjectDashboardSection' className="wrapper bg-gradient-primary">
        <div id="industryradar" className="container pb-10">
            <Tippy content='Important Industry Topics'><h2 className="text-uppercase main-header">Industry Radar</h2></Tippy> 
        </div>
        <div className="container">
          {/* <IndustryRadar data={pipelineData} /> */}
          <IndustryRadar data={pipelineData} />
        </div>
      </section>
    </div>
  );
}

export default PolicyRegulatory;
